import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Grid, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { CloudUpload, PlayArrow, Refresh, Assignment, Sync, Settings } from '@mui/icons-material';
import { orderProcessorAPI } from '../../services/api';
import { errMsg, fmtDateTime, isoDaysAgo, RUN_STATUS_LABEL } from './common';

interface Props {
  storeId: number;
  storeName: string;
  onOpenRun: (runId: number) => void;
}

type Kind = 'sales' | 'purchase' | 'inventory';

const normName = (v: string) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');

interface PendingUpload {
  kind: Kind;
  file: File;
  title: string;            // first line of the evitalRx report
  detectedStore?: string;   // store named in the title / file name, if any
  mismatch: boolean;        // detected store differs from the selected one
}

const UPLOADS: Array<{ kind: Kind; title: string; hint: string }> = [
  { kind: 'sales', title: 'Sales CSV', hint: 'evitalRx Sales report. Dates in the file replace existing rows for this store.' },
  { kind: 'purchase', title: 'Purchase CSV', hint: 'evitalRx Purchase report. Dates in the file replace existing rows; open POs are re-matched.' },
  { kind: 'inventory', title: 'Current Inventory CSV', hint: 'evitalRx Current Inventory report. Replaces the store snapshot and is the input for the optimizer run.' },
];

const DataStep: React.FC<Props> = ({ storeId, storeName, onOpenRun }) => {
  const [status, setStatus] = useState<any[]>([]);
  const [openRuns, setOpenRuns] = useState<any[]>([]);
  const [busy, setBusy] = useState<Kind | 'run' | 'commitment' | 'pos' | 'source' | null>(null);
  const [results, setResults] = useState<Partial<Record<Kind, any>>>({});
  const [error, setError] = useState<string | null>(null);
  const [cFrom, setCFrom] = useState(isoDaysAgo(7));
  const [cTo, setCTo] = useState(isoDaysAgo(0));
  const [pending, setPending] = useState<PendingUpload | null>(null);
  const [posFrom, setPosFrom] = useState('');   // blank = since the last sync
  const [posTo, setPosTo] = useState('');       // blank = today
  const [posResult, setPosResult] = useState<any>(null);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [sources, setSources] = useState<Array<{ store_id: number; store_name: string; source: 'evitalrx' | 'pos'; pos_from: string | null; opened_on: string | null; new_store_until: string | null; new_store_active: boolean }>>([]);

  const load = useCallback(async () => {
    try {
      const [s, runs] = await Promise.all([
        orderProcessorAPI.dataStatus(),
        orderProcessorAPI.listRuns({ store_id: storeId, from: isoDaysAgo(90) }),
      ]);
      setStatus(s);
      setOpenRuns(runs.filter((r) => ['draft', 'reviewed', 'distributors_assigned'].includes(r.status)));
    } catch (e) {
      setError(errMsg(e));
    }
  }, [storeId]);

  useEffect(() => { load(); setResults({}); setPosResult(null); }, [load]);

  // FirstShield POS → ETL tables (sales / purchase / current inventory) + optimizer snapshot
  const syncPos = async () => {
    setBusy('pos'); setError(null);
    try {
      const res = await orderProcessorAPI.syncPos(storeId, posFrom, posTo);
      setPosResult(res);
      setResults((r) => ({ ...r, inventory: { upload_id: res.inventory_upload_id } }));
      load();
    } catch (e) {
      setError(`POS sync failed — ${errMsg(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const openSources = async () => {
    try {
      const rows = await orderProcessorAPI.storeSources();
      setSources(rows.map((r) => ({ store_id: r.store_id, store_name: r.store_name, source: r.source, pos_from: r.pos_from, opened_on: r.opened_on, new_store_until: r.new_store_until, new_store_active: r.new_store_active })));
      setSourceOpen(true);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const saveSources = async () => {
    setBusy('source'); setError(null);
    try {
      const current = await orderProcessorAPI.storeSources();
      for (const s of sources) {
        const was = current.find((c) => c.store_id === s.store_id);
        if (was && was.source === s.source && (was.pos_from || null) === (s.source === 'pos' ? s.pos_from || null : null)
          && (was.opened_on || null) === (s.opened_on || null)) continue;
        await orderProcessorAPI.setStoreSource(s.store_id, { source: s.source, pos_from: s.source === 'pos' ? s.pos_from : null, opened_on: s.opened_on || null });
      }
      setSourceOpen(false);
      setResults({}); setPosResult(null);
      load();
    } catch (e) {
      setError(`Could not save data source — ${errMsg(e)}`);
    } finally {
      setBusy(null);
    }
  };

  // Step 1: read the report title and ask for confirmation (store shown prominently)
  const prepareUpload = async (kind: Kind, file: File | undefined) => {
    if (!file) return;
    let title = '';
    try { title = (await file.slice(0, 400).text()).replace(/^\uFEFF/, '').split(/\r?\n/)[0]; } catch { /* ignore */ }
    const hay = [normName(title), normName(file.name)];
    const named = status.filter((st) => normName(st.store_name) && hay.some((h) => h.includes(normName(st.store_name))));
    const mismatch = named.length > 0 && !named.some((st) => st.store_id === storeId);
    setPending({ kind, file, title, detectedStore: named[0]?.store_name, mismatch });
  };

  // Step 2: upload after confirmation; the server re-checks the store and answers 409 on a mismatch
  const upload = async (p: PendingUpload, forceMismatch: boolean) => {
    setBusy(p.kind); setError(null);
    try {
      const fn = p.kind === 'sales' ? orderProcessorAPI.uploadSales : p.kind === 'purchase' ? orderProcessorAPI.uploadPurchase : orderProcessorAPI.uploadInventory;
      const res = await fn(storeId, p.file, forceMismatch);
      setResults((r) => ({ ...r, [p.kind]: { ...res, file_name: p.file.name } }));
      setPending(null);
      load();
    } catch (e: any) {
      const d = e?.response?.data;
      if (e?.response?.status === 409 && d?.code === 'STORE_MISMATCH') {
        setPending({ ...p, mismatch: true, detectedStore: d.detected_store });
      } else {
        setPending(null);
        setError(`${p.kind} upload failed — ${errMsg(e)}`);
      }
    } finally {
      setBusy(null);
    }
  };

  const runOptimizer = async () => {
    const inv = results.inventory;
    if (!inv) return;
    setBusy('run'); setError(null);
    try {
      const run = await orderProcessorAPI.createOptimizerRun(storeId, inv.upload_id);
      onOpenRun(run.id);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const runCommitment = async () => {
    setBusy('commitment'); setError(null);
    try {
      const run = await orderProcessorAPI.createCommitmentRun(storeId, cFrom, cTo);
      onOpenRun(run.id);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const storeStatus = status.find((s) => s.store_id === storeId);
  const isPos = storeStatus?.source === 'pos';

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {openRuns.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Runs in progress for {storeName}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {openRuns.map((r) => (
              <Button key={r.id} size="small" variant="outlined" onClick={() => onOpenRun(r.id)}>
                {r.run_number} · {r.source} · {RUN_STATUS_LABEL[r.status] || r.status}
              </Button>
            ))}
          </Stack>
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Data freshness (all stores)</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<Settings />} onClick={openSources}>Data source</Button>
            <Button size="small" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          </Stack>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Store</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Last sales day</TableCell>
                <TableCell align="right">Bills that day</TableCell>
                <TableCell>Missing sales days (30d)</TableCell>
                <TableCell>Last purchase day</TableCell>
                <TableCell>Inventory snapshot</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {status.map((s) => (
                <TableRow key={s.store_id} selected={s.store_id === storeId}>
                  <TableCell><b>{s.store_name}</b></TableCell>
                  <TableCell>
                    {s.source === 'pos'
                      ? <Chip size="small" color="secondary" label={`POS since ${s.pos_from}`} title={s.last_pos_sync_at ? `Last POS sync ${fmtDateTime(s.last_pos_sync_at)}` : 'Not synced yet'} />
                      : <Chip size="small" variant="outlined" label="evitalRx" />}
                    {s.source === 'pos' && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {s.last_pos_sync_at ? `synced ${fmtDateTime(s.last_pos_sync_at)}` : 'not synced yet'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{s.last_sales_date || '—'}</TableCell>
                  <TableCell align="right">{s.last_day_bills ?? '—'}</TableCell>
                  <TableCell>
                    {s.missing_sales_days?.length
                      ? <Chip size="small" color="warning" label={`${s.missing_sales_days.length}: ${s.missing_sales_days.slice(0, 4).join(', ')}${s.missing_sales_days.length > 4 ? '…' : ''}`} />
                      : <Chip size="small" color="success" label="none" />}
                  </TableCell>
                  <TableCell>{s.last_purchase_date || '—'}</TableCell>
                  <TableCell>{s.inventory_rows ? `${s.inventory_rows} items · ${fmtDateTime(s.inventory_updated_at)}` : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {isPos && (
        <>
          <Typography variant="h6" gutterBottom>
            1. Sync from FirstShield POS for <Chip color="primary" label={storeName.toUpperCase()} sx={{ fontWeight: 700, fontSize: '1rem' }} />
          </Typography>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Copies this store’s POS bills (sales, net of returns), purchase invoices and current stock into the OMS tables,
                replacing those dates — the same tables the evitalRx uploads fill. Only bills from {storeStatus?.pos_from} onwards are synced.
                It also runs automatically every hour (7 AM–11 PM). Leave the dates blank to sync everything since the last sync.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField size="small" type="date" label="From (optional)" value={posFrom} onChange={(e) => setPosFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="To (optional)" value={posTo} onChange={(e) => setPosTo(e.target.value)} InputLabelProps={{ shrink: true }} />
                <Button variant="contained" disabled={!!busy} onClick={syncPos}
                  startIcon={busy === 'pos' ? <CircularProgress size={16} /> : <Sync />}>
                  {busy === 'pos' ? 'Syncing…' : 'Sync from POS'}
                </Button>
              </Stack>
              {!posResult && storeStatus?.last_pos_sync_at && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                  Last POS sync: {fmtDateTime(storeStatus.last_pos_sync_at)}
                </Typography>
              )}
              {posResult && (
                <Alert severity="success" sx={{ mt: 1.5 }}>
                  Synced <b>{posResult.from}</b> → <b>{posResult.to}</b><br />
                  Sales: {posResult.sales.bills} bills · {posResult.sales.lines} lines · {posResult.sales.units} units · ₹{Number(posResult.sales.amount).toLocaleString('en-IN')}
                  {' '}(replaced {posResult.sales.rows_deleted} rows over {posResult.sales.dates} days)<br />
                  Purchases: {posResult.purchases.invoices} invoices · {posResult.purchases.lines} lines · ₹{Number(posResult.purchases.amount).toLocaleString('en-IN')}<br />
                  Stock: {posResult.inventory.items} items ({posResult.inventory.items_in_stock} in stock) · {posResult.inventory.units} units
                  {posResult.receipts?.length ? <><br />Re-matched {posResult.receipts.length} open PO(s).</> : null}
                </Alert>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {!isPos && <>
      <Typography variant="h6" gutterBottom>
        1. Upload today’s evitalRx reports for <Chip color="primary" label={storeName.toUpperCase()} sx={{ fontWeight: 700, fontSize: '1rem' }} />
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {UPLOADS.map((u) => {
          const res = results[u.kind];
          const last = storeStatus?.last_uploads?.[u.kind];
          return (
            <Grid item xs={12} md={4} key={u.kind}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600}>{u.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{u.hint}</Typography>
                  <Button component="label" variant="contained" size="small" disabled={!!busy}
                    startIcon={busy === u.kind ? <CircularProgress size={16} /> : <CloudUpload />}>
                    {busy === u.kind ? 'Uploading…' : 'Choose CSV'}
                    <input hidden type="file" accept=".csv,text/csv"
                      onChange={(e) => { prepareUpload(u.kind, e.target.files?.[0]); e.target.value = ''; }} />
                  </Button>
                  {last && !res && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                      Last OMS upload: {last.file_name} · {fmtDateTime(last.created_at)}
                    </Typography>
                  )}
                  {res && (
                    <Alert severity="success" sx={{ mt: 1.5 }}>
                      <b>{res.file_name}</b><br />
                      {u.kind !== 'inventory' && <>Dates: {res.dates?.join(', ')}<br />Replaced {res.rows_deleted} · inserted {res.rows_inserted}<br /></>}
                      {u.kind === 'inventory' && <>Snapshot: {res.rows_inserted} items (replaced {res.rows_deleted})<br />
                        CSV rows {res.rows_in_csv}{res.unmatched ? ` · ${res.unmatched} not matched to a medicine master` : ''}<br /></>}
                      {res.manufacturer_inserted ? `New medicines: ${res.manufacturer_inserted}. ` : ''}
                      {res.distributor_inserted ? `New distributors: ${res.distributor_inserted}. ` : ''}
                      {res.receipts?.length ? `Re-matched ${res.receipts.length} open PO(s).` : ''}
                    </Alert>
                  )}
                  {u.kind === 'inventory' && res?.unmatched_rows?.length > 0 && (
                    <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
                      Unmatched (left out, same as the Streamlit tool): {res.unmatched_rows.slice(0, 5).map((r: any) => r.medicine_name).join(', ')}
                      {res.unmatched_rows.length > 5 ? '…' : ''}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      </>}

      <Typography variant="h6" gutterBottom>2. Start a run</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600}>Order Optimizer</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Runs every optimizer rule (velocity categories, min/max, duplicate consolidation, 85% budget) on the inventory just uploaded.
                {isPos ? 'Uses the stock from the POS sync above, with sales and purchases just synced.' : 'Upload today’s Sales and Purchase first so velocity and budget are current.'}
              </Typography>
              <Button variant="contained" color="primary" disabled={!results.inventory || !!busy} onClick={runOptimizer}
                startIcon={busy === 'run' ? <CircularProgress size={16} /> : <PlayArrow />}>
                {busy === 'run' ? 'Running rules…' : 'Run Order Optimizer'}
              </Button>
              {!results.inventory && <Typography variant="caption" display="block" sx={{ mt: 1 }}>{isPos ? 'Click “Sync from POS” above to enable.' : 'Upload the Current Inventory CSV above to enable.'}</Typography>}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600}>Customer orders only (no optimizer)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Usual path: run the Order Optimizer, then use “Add customer orders” in its review step so items in both are reviewed together.
                Use this only on days without an inventory upload. Quantities are strips; linked orders stay pending until you mark them ordered from the PO or the customer order.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField size="small" type="date" label="From" value={cFrom} onChange={(e) => setCFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="To" value={cTo} onChange={(e) => setCTo(e.target.value)} InputLabelProps={{ shrink: true }} />
                <Button variant="outlined" disabled={!!busy} onClick={runCommitment}
                  startIcon={busy === 'commitment' ? <CircularProgress size={16} /> : <Assignment />}>Create run</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Divider sx={{ mt: 3 }} />

      <Dialog open={!!pending} onClose={() => (busy ? undefined : setPending(null))} maxWidth="sm" fullWidth>
        {pending && (
          <>
            <DialogTitle sx={{ bgcolor: pending.mismatch ? 'error.main' : undefined, color: pending.mismatch ? 'error.contrastText' : undefined }}>
              {pending.mismatch ? '⚠ Store mismatch — check before uploading' : 'Confirm upload'}
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary">Upload</Typography>
              <Typography variant="h6" gutterBottom>{pending.kind === 'inventory' ? 'Current Inventory' : pending.kind === 'sales' ? 'Sales' : 'Purchase'} → store <b>{storeName.toUpperCase()}</b></Typography>
              <Typography variant="body2"><b>File:</b> {pending.file.name}</Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}><b>Report title:</b> {pending.title || '—'}</Typography>
              {pending.mismatch ? (
                <Alert severity="error">
                  This file looks like <b>{String(pending.detectedStore).toUpperCase()}</b> data, but the selected store is <b>{storeName.toUpperCase()}</b>.
                  Uploading replaces {storeName}’s {pending.kind === 'inventory' ? 'inventory snapshot' : `${pending.kind} for every date in the file`}.
                  Change the store at the top of the page if this is the wrong one.
                </Alert>
              ) : (
                <Alert severity={pending.detectedStore ? 'success' : 'warning'}>
                  {pending.detectedStore
                    ? <>The report is for <b>{String(pending.detectedStore).toUpperCase()}</b> — matches the selected store.</>
                    : <>The report doesn’t name a store — make sure this is <b>{storeName.toUpperCase()}</b>’s file.</>}
                  {pending.kind !== 'inventory' ? ' Existing rows for the dates in the file will be replaced.' : ' The store’s inventory snapshot will be replaced.'}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPending(null)} disabled={!!busy}>Cancel</Button>
              {pending.mismatch ? (
                <Button color="error" variant="outlined" disabled={!!busy} onClick={() => upload(pending, true)}>
                  Upload anyway to {storeName}
                </Button>
              ) : (
                <Button variant="contained" disabled={!!busy} onClick={() => upload(pending, false)}
                  startIcon={busy ? <CircularProgress size={16} /> : undefined}>
                  Upload to {storeName}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={sourceOpen} onClose={() => (busy ? undefined : setSourceOpen(false))} maxWidth="md" fullWidth>
        <DialogTitle>Data source per store</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            evitalRx stores use the CSV uploads (and the hourly evitalRx scraper). FirstShield POS stores are synced from our own POS;
            only POS bills on or after “POS from” are copied, and the evitalRx scraper stops for that store.
            For 90 days after “Opened on”, the optimizer also refills whatever sold in the last 30 days (new-store refill, outside the budget).
          </Typography>
          {sources.map((s, idx) => (
            <Stack key={s.store_id} direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
              <Typography sx={{ width: 130, fontWeight: 600 }}>{s.store_name}</Typography>
              <TextField select size="small" label="Source" value={s.source} sx={{ width: 190 }}
                onChange={(e) => setSources((arr) => arr.map((x, i) => (i === idx ? { ...x, source: e.target.value as 'evitalrx' | 'pos' } : x)))}>
                <MenuItem value="evitalrx">evitalRx (CSV)</MenuItem>
                <MenuItem value="pos">FirstShield POS</MenuItem>
              </TextField>
              {s.source === 'pos' && (
                <TextField size="small" type="date" label="POS from" value={s.pos_from || ''} InputLabelProps={{ shrink: true }}
                  onChange={(e) => setSources((arr) => arr.map((x, i) => (i === idx ? { ...x, pos_from: e.target.value } : x)))} />
              )}
              <TextField size="small" type="date" label="Opened on" value={s.opened_on || ''} InputLabelProps={{ shrink: true }}
                helperText={s.new_store_active && s.new_store_until ? `New-store refill until ${s.new_store_until}` : ' '}
                onChange={(e) => setSources((arr) => arr.map((x, i) => (i === idx ? { ...x, opened_on: e.target.value || null } : x)))} />
            </Stack>
          ))}
          <Alert severity="warning" sx={{ mt: 1 }}>
            Switching a store to POS replaces its OMS sales and purchase rows from the “POS from” date onwards with POS data at the next sync.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSourceOpen(false)} disabled={!!busy}>Cancel</Button>
          <Button variant="contained" onClick={saveSources} disabled={!!busy || sources.some((s) => s.source === 'pos' && !s.pos_from)}
            startIcon={busy === 'source' ? <CircularProgress size={16} /> : undefined}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataStep;
